import { Layout, Menu, Drawer, Button, ConfigProvider } from "antd";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import SidebarFooter from "../Slider/SidebarFooter";
import { sidebarTheme } from "../../theme/sidebarTheme";
import { sidebar } from "../../theme/tokens";

const { Sider } = Layout;

/**
 * Menu điều hướng (dark navy) — dùng chung cho Sider desktop & Drawer mobile.
 *
 * `inlineIndent` cố định 12 cho cả hai: phần thụt lề của mục con được canh theo
 * đúng con số này trong `index.css` (12 + icon 16 + 10 = 38px, xem chú thích ở
 * đó), nên hai nơi lệch nhau là chữ mục con lệch khỏi chữ mục cha.
 */
function NavMenu({ items, openKeys, onOpenChange, selectedKeys }) {
  return (
    <Menu
      className="cms-sidebar-menu"
      mode="inline"
      theme="dark"
      inlineIndent={12}
      openKeys={openKeys}
      onOpenChange={onOpenChange}
      /*
       * Kiểm soát bằng URL thay vì để AntD tự nhớ item vừa bấm: click thì hai
       * cách như nhau, nhưng F5 / vào bằng link trực tiếp thì chỉ cách này còn
       * highlight. Điều hướng đi qua `<Link>` nên pathname đổi ngay khi bấm.
       */
      selectedKeys={selectedKeys}
      items={items}
      style={{ borderInlineEnd: "none", background: "transparent" }}
    />
  );
}

/**
 * Khung điều hướng bên trái. Desktop = Sider thu gọn được; mobile = Drawer.
 * Style navy đẩy qua ConfigProvider(sidebarTheme) — không inject <style> nữa.
 */
export default function SidebarNav({
  collapsed,
  onToggle,
  isMobile,
  drawerVisible,
  onDrawerClose,
  menuItems,
  openKeys,
  onOpenChange,
  selectedKeys,
}) {
  if (isMobile) {
    return (
      <ConfigProvider theme={sidebarTheme}>
        <Drawer
          placement="left"
          closable={false}
          onClose={onDrawerClose}
          open={drawerVisible}
          width={256}
          styles={{ body: { padding: 0, background: sidebar.bg } }}
        >
          <div style={{ display: "flex", flexDirection: "column", height: "100%", background: sidebar.bg }}>
            <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", paddingTop: 8 }}>
              {menuItems?.length > 0 && (
                <NavMenu
                  items={menuItems}
                  openKeys={openKeys}
                  onOpenChange={onOpenChange}
                  selectedKeys={selectedKeys}
                />
              )}
            </div>
            <SidebarFooter collapsed={false} isDarkMode />
          </div>
        </Drawer>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={sidebarTheme}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={236}
        collapsedWidth={68}
        style={{ background: sidebar.bg, borderRight: `1px solid ${sidebar.border}`, flexShrink: 0 }}
      >
        <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Đỉnh: nút thu gọn (canh cao 64px để thẳng hàng với topbar) */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: collapsed ? "center" : "flex-end",
              height: 64,
              padding: collapsed ? 0 : "0 12px",
              flexShrink: 0,
              borderBottom: `1px solid ${sidebar.border}`,
            }}
          >
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={onToggle}
              style={{
                fontSize: 16,
                width: 36,
                height: 36,
                color: sidebar.text,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 8,
              }}
            />
          </div>

          {/* Menu (vùng cuộn) */}
          <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
            {menuItems?.length > 0 && (
              <NavMenu
                items={menuItems}
                openKeys={openKeys}
                onOpenChange={onOpenChange}
                selectedKeys={selectedKeys}
              />
            )}
          </div>

          {/* Đáy: card nâng cấp + copyright (luôn ở tông navy) */}
          <SidebarFooter collapsed={collapsed} isDarkMode />
        </div>
      </Sider>
    </ConfigProvider>
  );
}
